import { env } from "@/config/env";

import {
  BRIDGE_ERROR_CODES,
  BRIDGE_PROTOCOL_VERSION,
  BRIDGE_RESULT_GUARDS,
  parseBridgeIncoming,
  type BridgeEvent,
  type BridgeEventType,
  type BridgeMethod,
  type BridgeParams,
  type BridgeResult,
} from "./contract";

export class BridgeError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "BridgeError";
    this.code = code;
  }
}

type PendingCall = {
  readonly resolve: (value: unknown) => void;
  readonly reject: (error: BridgeError) => void;
  readonly timer: ReturnType<typeof setTimeout>;
};

const pending = new Map<string, PendingCall>();

type EventListeners = {
  [T in BridgeEventType]: Set<(event: Extract<BridgeEvent, { type: T }>) => void>;
};

const listeners: EventListeners = {
  "session.changed": new Set(),
  "app.foreground": new Set(),
  "app.background": new Set(),
  "nav.backPressed": new Set(),
};

let requestCounter = 0;

function nextRequestId(): string {
  requestCounter += 1;
  return `w-${Date.now().toString(36)}-${requestCounter}`;
}

/** True when running inside the native shell rather than a plain browser tab. */
export function isNativeHost(): boolean {
  return typeof window.ReactNativeWebView?.postMessage === "function";
}

function dispatchEvent(event: BridgeEvent): void {
  switch (event.type) {
    case "session.changed":
      return notify(listeners[event.type], event);
    case "app.foreground":
      return notify(listeners[event.type], event);
    case "app.background":
      return notify(listeners[event.type], event);
    case "nav.backPressed": {
      const handlers = listeners[event.type];
      if (handlers.size === 0) {
        // Bridge is installed before the app mounts (and stays installed when
        // bootstrap/session fails). With nobody listening, close the WebView
        // the same way buildBackPressScript's no-receiver fallback does —
        // otherwise the native back button is silently dead on the error
        // screen in main.tsx.
        requestNativeBack();
        return;
      }
      return notify(handlers, event);
    }
  }
}

/**
 * Fire-and-forget `nav.back`. Used when the page cannot own history (boot
 * failed). Does not wait for a response — the WebView may be torn down the
 * moment native handles the message.
 */
function requestNativeBack(): void {
  const nativeWebView = window.ReactNativeWebView;
  if (nativeWebView === undefined) return;

  nativeWebView.postMessage(
    JSON.stringify({
      v: BRIDGE_PROTOCOL_VERSION,
      id: "native-back",
      method: "nav.back",
      params: null,
    }),
  );
}

function notify<E>(handlers: Set<(event: E) => void>, event: E): void {
  for (const handler of handlers) handler(event);
}

/**
 * Removes a pending call and stops its timeout. Returns null when there is
 * nothing pending — a response that arrived after its timeout, or a duplicate.
 */
function takePending(id: string): PendingCall | null {
  const call = pending.get(id);
  if (call === undefined) return null;

  clearTimeout(call.timer);
  pending.delete(id);
  return call;
}

function receive(rawMessage: string): void {
  let json: unknown;
  try {
    json = JSON.parse(rawMessage);
  } catch {
    console.error("[bridge] native sent a message that is not valid JSON");
    return;
  }

  const message = parseBridgeIncoming(json);
  if (message === null) {
    console.error("[bridge] native sent a message that fails the contract", json);
    return;
  }

  if (!("id" in message)) {
    dispatchEvent(message);
    return;
  }

  const call = takePending(message.id);
  if (call === null) return;

  if (message.ok) {
    call.resolve(message.result);
  } else {
    call.reject(new BridgeError(message.error.code, message.error.message));
  }
}

/**
 * Installs the inbound channel. Call once, before rendering, so no message
 * pushed by the shell during start-up is lost.
 */
export function installBridge(): void {
  window.__SGC_RECEIVE__ = receive;
}

/**
 * Subscribes to a native event. Returns the unsubscribe function.
 */
export function onBridgeEvent<T extends BridgeEventType>(
  type: T,
  handler: (event: Extract<BridgeEvent, { type: T }>) => void,
): () => void {
  listeners[type].add(handler);
  return () => {
    listeners[type].delete(handler);
  };
}

/**
 * Calls a native method.
 *
 * Rejects with a `BridgeError` when there is no native shell, when the shell
 * does not answer within `VITE_BRIDGE_TIMEOUT_MS` (which is what happens on an
 * older app version that does not implement the method), or when the shell
 * reports a failure.
 */
export function call<M extends BridgeMethod>(
  method: M,
  ...args: BridgeParams<M> extends void ? [] : [params: BridgeParams<M>]
): Promise<BridgeResult<M>> {
  const nativeWebView = window.ReactNativeWebView;

  if (nativeWebView === undefined) {
    return Promise.reject(
      new BridgeError(
        BRIDGE_ERROR_CODES.unavailable,
        `No native shell available for "${method}".`,
      ),
    );
  }

  const params = args.length > 0 ? args[0] : undefined;
  const id = nextRequestId();
  const isExpectedResult: (value: unknown) => boolean =
    BRIDGE_RESULT_GUARDS[method];

  return new Promise<BridgeResult<M>>((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      reject(
        new BridgeError(
          BRIDGE_ERROR_CODES.timeout,
          `Native shell did not answer "${method}" within ${String(env.bridgeTimeoutMs)}ms.`,
        ),
      );
    }, env.bridgeTimeoutMs);

    pending.set(id, {
      resolve: (value) => {
        if (!isExpectedResult(value)) {
          reject(
            new BridgeError(
              BRIDGE_ERROR_CODES.malformedResponse,
              `Native shell answered "${method}" with an unexpected shape.`,
            ),
          );
          return;
        }
        // Safe by construction: `value` was just checked by this method's own
        // guard, which is typed as `value is BridgeResult<M>`.
        resolve(value as BridgeResult<M>);
      },
      reject,
      timer,
    });

    nativeWebView.postMessage(
      JSON.stringify({ v: BRIDGE_PROTOCOL_VERSION, id, method, params }),
    );
  });
}
